import React, { Component } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchReverseRaffleEvent,
  fetchReverseRaffleList,
} from "./reverseRaffleApi";
import { resolveMediaUrl } from "../Helper/mediaUrl";
import "./ReverseRaffleEventPage.css";

/**
 * Public Reverse Raffle presentation — list + detail.
 * No live purchase gateway (Product/Legal gate; admin-recorded entries only).
 * Ticket numbers are sequential; drawing order is server CSPRNG — FE never shuffles.
 */

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return "—";
  const cur = currency || "USD";
  const n = typeof amount === "number" ? amount : parseFloat(amount);
  if (Number.isNaN(n)) return `${amount} ${cur}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
    }).format(n);
  } catch (e) {
    return `${n.toFixed(2)} ${cur}`;
  }
}

function prizeLabel(prize, currency) {
  if (!prize) return "—";
  const parts = [];
  if (prize.description) parts.push(prize.description);
  if (prize.amount != null && prize.amount !== "") {
    parts.push(formatMoney(prize.amount, currency));
  }
  return parts.length ? parts.join(" · ") : "—";
}

/** Neutral copy when jurisdiction is not enabled — do not claim illegality. */
const UNAVAILABLE_IN_AREA =
  "This event is not currently available in your area.";

function stateAvailability(event) {
  const sa = (event && event.state_availability) || {};
  const hasAny =
    sa.has_any_approved_state === true ||
    (Array.isArray(event && event.approved_states) &&
      event.approved_states.length > 0);
  const message =
    (typeof sa.unavailable_message === "string" && sa.unavailable_message) ||
    UNAVAILABLE_IN_AREA;
  return { hasAny, message };
}

class ReverseRaffleEventPage extends Component {
  state = {
    loading: true,
    event: null,
    list: [],
    error: false,
    errorMessage: "",
    notFound: false,
  };

  loadGeneration = 0;

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "";
    const nextSlug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    if (prevSlug !== nextSlug) {
      this.load();
    }
  }

  slug() {
    return this.props.match && this.props.match.params
      ? this.props.match.params.slug
      : "";
  }

  async load() {
    const slug = this.slug();
    const generation = ++this.loadGeneration;
    this.setState({
      loading: true,
      error: false,
      errorMessage: "",
      notFound: false,
    });
    if (!slug) {
      const result = await fetchReverseRaffleList();
      if (generation !== this.loadGeneration) {
        return;
      }
      this.setState({
        loading: false,
        list: result.events || [],
        event: null,
        error: !!result.error,
        errorMessage: result.message || "",
        notFound: false,
      });
      return;
    }
    const result = await fetchReverseRaffleEvent(slug);
    if (generation !== this.loadGeneration) {
      return;
    }
    if (result.event) {
      this.setState({
        loading: false,
        event: result.event,
        error: false,
        notFound: false,
        errorMessage: "",
        list: [],
      });
      return;
    }
    const isNotFound =
      !result.error || /not found/i.test(String(result.message || ""));
    this.setState({
      loading: false,
      event: null,
      error: !isNotFound,
      notFound: isNotFound,
      errorMessage: result.message || "",
      list: [],
    });
  }

  renderAvailabilityCopy(event) {
    const { hasAny, message } = stateAvailability(event);
    if (!hasAny) {
      return <p className="rr-availability">{message}</p>;
    }
    if (event.accepts_entries) {
      return (
        <p>
          Entries are open for areas configured for this event. Online card
          purchase is not enabled until Product/Legal clearance — contact
          Pentecost+ / Admin for entry instructions. A Pentecost+ handle is
          required. If this event is not available where you are, you will
          see: “{message}”
        </p>
      );
    }
    return <p>Entries are not currently being accepted for this event.</p>;
  }

  renderList() {
    const { list, error, errorMessage } = this.state;
    return (
      <div className="rr-page">
        <Helmet>
          <title>Reverse Raffle · Pentecost+</title>
        </Helmet>
        <header className="rr-header">
          <p className="rr-brand">Pentecost+</p>
          <h1 className="rr-title">Reverse Raffle</h1>
          <p className="rr-lede">
            Tickets are assigned sequentially. Drawing order is a separate
            secure random permutation after every ticket is sold.
          </p>
        </header>

        {error ? (
          <div className="rr-alert" role="alert">
            <p>{errorMessage || "We couldn’t load Reverse Raffle events."}</p>
            <button
              type="button"
              className="rr-retry"
              onClick={() => this.load()}
            >
              Retry
            </button>
          </div>
        ) : null}

        <ul className="rr-list">
          {!error && list.length === 0 ? (
            <li className="rr-empty">No public events right now.</li>
          ) : (
            list.map((item) => (
              <li key={item.uuid || item.id} className="rr-list-item">
                <div>
                  <Link
                    className="rr-list-link"
                    to={`/reverse-raffle/${item.slug}`}
                  >
                    {item.name}
                  </Link>
                  <div className="rr-meta">
                    {formatMoney(item.ticket_price, item.currency)}
                    {item.capacity != null
                      ? ` · ${item.sold_count || 0}/${item.capacity} sold`
                      : ""}
                    {item.accepts_entries ? " · Entries open" : ""}
                  </div>
                </div>
                <span
                  className={
                    "rr-badge" + (item.accepts_entries ? " rr-badge--open" : "")
                  }
                >
                  {item.status_label}
                </span>
              </li>
            ))
          )}
        </ul>
      </div>
    );
  }

  renderEvent() {
    const { event } = this.state;
    const image = event.image_path
      ? resolveMediaUrl(event.image_path)
      : null;
    const currency = event.currency || "USD";
    const history = Array.isArray(event.draw_history)
      ? event.draw_history
      : [];
    const prizes = Array.isArray(event.prize_schedule)
      ? event.prize_schedule
      : [];
    const isDrawing =
      event.status === "drawing" ||
      event.status === "drawing_complete" ||
      event.status === "completed";
    const soldOutPending =
      event.is_sold_out &&
      (event.status === "closed" || event.status === "open");

    return (
      <div className="rr-page">
        <Helmet>
          <title>{event.name} · Reverse Raffle</title>
        </Helmet>
        {image ? (
          <div
            className="rr-hero"
            style={{ backgroundImage: `url(${image})` }}
            role="img"
            aria-label={event.name}
          />
        ) : null}
        <header className="rr-header">
          <p className="rr-brand">Pentecost+</p>
          <p
            className={
              "rr-status" + (event.accepts_entries ? " rr-status--open" : "")
            }
          >
            {event.status_label}
          </p>
          <h1 className="rr-title">{event.name}</h1>
          {event.description ? (
            <p className="rr-lede">{event.description}</p>
          ) : null}
        </header>

        <section className="rr-section">
          <h2>Availability</h2>
          <dl className="rr-dl">
            <div>
              <dt>Capacity</dt>
              <dd>{event.capacity}</dd>
            </div>
            <div>
              <dt>Sold</dt>
              <dd>{event.sold_count}</dd>
            </div>
            <div>
              <dt>Remaining</dt>
              <dd>{event.remaining_count}</dd>
            </div>
            <div>
              <dt>Ticket range</dt>
              <dd>
                {event.start_number}–{event.end_number}
              </dd>
            </div>
            <div>
              <dt>Ticket price</dt>
              <dd>{formatMoney(event.ticket_price, currency)}</dd>
            </div>
            <div>
              <dt>Sales deadline</dt>
              <dd>{formatWhen(event.sales_deadline_at)}</dd>
            </div>
            <div>
              <dt>Starts</dt>
              <dd>{formatWhen(event.starts_at)}</dd>
            </div>
            <div>
              <dt>Ends</dt>
              <dd>{formatWhen(event.ends_at)}</dd>
            </div>
          </dl>
          {soldOutPending ? (
            <p className="rr-meta" style={{ marginTop: "1rem" }}>
              All tickets are sold. Drawing begins when an Administrator starts
              the secure random permutation — not in purchase order.
            </p>
          ) : null}
        </section>

        {prizes.length > 0 ? (
          <section className="rr-section">
            <h2>Prizes by pull position</h2>
            <ul className="rr-prize-list">
              {prizes.map((p) => (
                <li key={p.pull_position}>
                  Pull #{p.pull_position}: {prizeLabel(p, currency)}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {isDrawing || history.length > 0 ? (
          <section className="rr-section">
            <h2>
              {event.status === "drawing"
                ? "Draw history (progressive)"
                : "Draw history"}
            </h2>
            {history.length === 0 ? (
              <p className="rr-meta">No pulls revealed yet.</p>
            ) : (
              <table className="rr-history">
                <thead>
                  <tr>
                    <th>Pull #</th>
                    <th>Ticket #</th>
                    <th>Handle</th>
                    <th>Prize</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.pull_number}>
                      <td>{row.pull_number}</td>
                      <td>{row.ticket_number}</td>
                      <td>
                        {row.handle ? `@${row.handle}` : "—"}
                      </td>
                      <td>{prizeLabel(row.prize, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ) : null}

        <section className="rr-section">
          <h2>Terms &amp; eligibility</h2>
          {event.terms_text ? (
            <p className="rr-terms">{event.terms_text}</p>
          ) : (
            <p className="rr-terms">
              Terms have not been published for this event.
            </p>
          )}
          <p className="rr-meta">
            A Pentecost+ handle is required for participation. Public history
            shows handles only — never email.
          </p>
          {event.compliance_notice ? (
            <p className="rr-compliance">{event.compliance_notice}</p>
          ) : null}
        </section>

        <section className="rr-section">
          {this.renderAvailabilityCopy(event)}
          <Link className="rr-back" to="/reverse-raffle">
            All Reverse Raffle events
          </Link>
        </section>
      </div>
    );
  }

  renderDetailFailure() {
    const { error, notFound, errorMessage } = this.state;
    const title = notFound ? "Event not found" : "Unable to load event";
    const helmetTitle = notFound
      ? "Event not found · Reverse Raffle"
      : "Unable to load · Reverse Raffle";
    return (
      <div className="rr-page">
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
        <h1 className="rr-title">{title}</h1>
        <p className="rr-lede" role={error ? "alert" : undefined}>
          {errorMessage ||
            (notFound
              ? "This Reverse Raffle event isn’t available."
              : "We couldn’t load this Reverse Raffle event. Please try again.")}
        </p>
        {error || notFound ? (
          <button
            type="button"
            className="rr-retry"
            onClick={() => this.load()}
          >
            Retry
          </button>
        ) : null}{" "}
        <Link className="rr-back" to="/reverse-raffle">
          Back to Reverse Raffle
        </Link>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="rr-page rr-loading" role="status">
          <Helmet>
            <title>Loading · Reverse Raffle</title>
          </Helmet>
          Loading…
        </div>
      );
    }
    if (!this.slug()) {
      return this.renderList();
    }
    if (this.state.error || this.state.notFound || !this.state.event) {
      return this.renderDetailFailure();
    }
    return this.renderEvent();
  }
}

export default ReverseRaffleEventPage;
